import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { Typography, Button } from "@mui/material";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { enqueueSnackbar } from 'notistack';
import DividerLabel from "../../CommonComponents/DividerLabel";
import { StorageGroup, TargetStorage } from "../../../../types/targetStorageTypes";
import S3StorageForm from "./TargetAccountForms/S3StorageForm";
import AzureStorageForm from "./TargetAccountForms/AzureStorageForm";
import SFTPStorageForm from "./TargetAccountForms/SFTPStorageForm";

import { getStorages, createStorage, updateStorage, deleteStorage } from "../../../../queries/targetStorageQueries";

interface TargetStorageSectionProps {}

const defaultS3: TargetStorage = { id: "", name: "", type: "s3", bucket_name: "", region: "", prefix: "", access_key: "", secret_key: "" };
const defaultAzure: TargetStorage = { id: "", name: "", type: "azure", account_name: "", container_name: "", prefix: "", sas_token: "" };
const defaultSFTP: TargetStorage = { id: "", name: "", type: "sftp", host: "", port: 22, username: "", remote_path: "", password: "" };

const TargetStorageSection: React.FC<TargetStorageSectionProps> = () => {
    const [storageGroups, setStorageGroups] = useState<StorageGroup[]>([
        { type: 's3', label: 'Amazon S3', accounts: [] },
        { type: 'azure', label: 'Azure Blob', accounts: [] },
        { type: 'sftp', label: 'SFTP Server', accounts: [] }
    ]);

    async function loadStorages() {
        try {
            const fetchedStorages: TargetStorage[] = await getStorages();

            const newGroups = [...storageGroups];
            newGroups.forEach(group => group.accounts = []);

            fetchedStorages.forEach(storage => {
                const group = newGroups.find(g => g.type === storage.type);
                if (group) {
                    group.accounts.push({ ...storage, mode: "readonly" });
                }
            });
            setStorageGroups(newGroups);
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Problems loading the storage accounts');
        }
    }

    useEffect(() => {
        loadStorages();
    }, []);

    const handleAddAccount = (groupIndex: number) => {
        const tempGroups = [...storageGroups];
        const group = tempGroups[groupIndex];

        let newAccount: TargetStorage;
        if (group.type === 's3') newAccount = { ...defaultS3, mode: "updatable" };
        else if (group.type === 'azure') newAccount = { ...defaultAzure, mode: "updatable" };
        else newAccount = { ...defaultSFTP, mode: "updatable" };

        group.accounts.unshift(newAccount);
        setStorageGroups(tempGroups);
    };

    const handleFieldChange = (groupIndex: number, accountIndex: number, field: string, value: any) => {
        const tempGroups = [...storageGroups];
        const account = tempGroups[groupIndex].accounts[accountIndex] as any;
        account[field] = value;
        setStorageGroups(tempGroups);
    };

    const handleAction = async (actionType: 'create' | 'update' | 'delete', groupIndex: number, accountIndex: number) => {
        const tempGroups = [...storageGroups];
        const account = tempGroups[groupIndex].accounts[accountIndex];

        try {
            if (actionType === 'create') {
                const created = await createStorage(account);
                tempGroups[groupIndex].accounts[accountIndex] = { ...created, mode: 'readonly' };
                enqueueSnackbar("Storage account created");
            } else if (actionType === 'update') {
                await updateStorage(account.id!, account);
                tempGroups[groupIndex].accounts[accountIndex].mode = 'readonly';
                enqueueSnackbar("Storage account updated");
            } else if (actionType === 'delete') {
                if (account.id) await deleteStorage(account.id);
                tempGroups[groupIndex].accounts.splice(accountIndex, 1);
                enqueueSnackbar("Storage account deleted");
            }
            setStorageGroups(tempGroups);
        } catch (e) {
            enqueueSnackbar(`${actionType} operation failed`);
        }
    };

    const renderForm = (group: StorageGroup, account: TargetStorage, groupIndex: number, accountIndex: number) => {
        const commonProps = {
            account,
            mode: account.mode || 'readonly',
            onFieldChange: (field: string, value: any) => handleFieldChange(groupIndex, accountIndex, field, value),
            onCreate: () => handleAction('create', groupIndex, accountIndex),
            onUpdate: () => handleAction('update', groupIndex, accountIndex),
            onDelete: () => handleAction('delete', groupIndex, accountIndex),
            onChangeMode: (mode: 'readonly' | 'updatable') => handleFieldChange(groupIndex, accountIndex, 'mode', mode)
        };

        if (group.type === 's3') return <S3StorageForm {...commonProps} />;
        if (group.type === 'azure') return <AzureStorageForm {...commonProps} />;
        if (group.type === 'sftp') return <SFTPStorageForm {...commonProps} />;
        return null;
    };

    return (
        <Box sx={{ width: '100%' }}>
            <DividerLabel
                my={1}
                label={
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontSize: '1.25rem',
                        fontWeight: 'bold',
                        textTransform: 'capitalize'
                    }}>
                        Target Storages
                    </span>
                }
            />
            <Box>
                {storageGroups.map((group, groupIndex) => (
                    <Box key={group.type} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ flexGrow: 1 }}>
                                <DividerLabel
                                    label={
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', fontSize: '1.25rem', fontWeight: 'bold' }}>
                                            {group.label}
                                        </span>
                                    }
                                />
                            </Box>
                            <Button
                                variant="contained"
                                onClick={() => handleAddAccount(groupIndex)}
                                sx={{ whiteSpace: 'nowrap', width: '150px' }}
                                startIcon={<AddCircleOutlineOutlinedIcon />}
                            >
                                Add Account
                            </Button>
                        </Box>

                        <Box sx={{ mt: 1 }}>
                            {group.accounts.map((account, accountIndex) => (
                                <Box key={accountIndex} sx={{ mt: 2 }}>
                                    {renderForm(group, account, groupIndex, accountIndex)}
                                    {accountIndex < group.accounts.length - 1 && (
                                        <Box sx={{ mt: 3, mb: 1 }}>
                                            <DividerLabel label=" " />
                                        </Box>
                                    )}
                                </Box>
                            ))}
                            {group.accounts.length === 0 && (
                                <Typography color="text.secondary" sx={{ mt: 2 }}>
                                    No account for this storage.
                                </Typography>
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default TargetStorageSection;